import { Injectable, Signal, computed, signal } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import type {
  Meter,
  MeterReading,
  MeterDocument,
  CreateMeterRequest,
  UpdateMeterRequest,
  AddMeterReadingRequest,
  ContractHolder
} from '../models/meter.model';
import { toStoredMeterProvider } from '../models/meter.model';
import type { IMetersStore } from './meters-store.interface';

/**
 * In-memory implementation of `IMetersStore`.
 *
 * Used by:
 * - Storybook/demos that don't need a Firebase connection
 * - Unit tests for consumers of `METERS_STORE`
 * - The `?store=mock` URL override for QA
 *
 * Data is seeded with a small fixture keyed by `propertyUID` so a page refresh
 * lands on something useful. Writes mutate the in-memory map.
 */
@Injectable()
export class MetersMockStore implements IMetersStore {
  private readonly _currentPropertyUID = signal<string | null>(null);
  private readonly _metersByProperty = signal<ReadonlyMap<string, readonly Meter[]>>(seed());
  private readonly _loading = signal(false);
  private readonly _lastError = signal<Error | null>(null);

  readonly currentPropertyUID = this._currentPropertyUID.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly lastError = this._lastError.asReadonly();

  readonly meters: Signal<readonly Meter[]> = computed(() => {
    const pid = this._currentPropertyUID();
    if (!pid) return [];
    return this._metersByProperty().get(pid) ?? [];
  });

  readonly totalCount = computed(() => this.meters().length);

  readonly metersByType = computed<Readonly<Record<string, number>>>(() =>
    this.meters().reduce<Record<string, number>>((acc, m) => {
      acc[m.type] = (acc[m.type] || 0) + 1;
      return acc;
    }, {})
  );

  readonly activeMeters = computed(() => this.meters().filter(m => m.status === 'active').length);
  readonly tenantMeters = computed(() => this.meters().filter(m => m.contractHolder === 'tenant').length);
  readonly ownerMeters = computed(() => this.meters().filter(m => m.contractHolder === 'owner').length);

  readonly metersNeedingAttention = computed(() => {
    const meters = this.meters();
    const threeMonthsAgo = Date.now() - 90 * 24 * 60 * 60 * 1000;
    return {
      noRecentReadings: meters.filter(m => {
        if (!m.currentReadingDate) return true;
        return m.currentReadingDate.toMillis() < threeMonthsAgo;
      }),
      inactive: meters.filter(m => m.status !== 'active')
    };
  });

  loadForProperty(propertyUID: string): void {
    if (this._currentPropertyUID() === propertyUID) return;
    this._currentPropertyUID.set(propertyUID);
    this._lastError.set(null);
  }

  async refresh(): Promise<void> {
    this._loading.set(true);
    await Promise.resolve();
    this._loading.set(false);
  }

  async createMeter(request: CreateMeterRequest): Promise<string> {
    const now = Timestamp.now();
    const meterUID = `mock-meter-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

    const initialReadings: MeterReading[] = [];
    if (request.currentReading !== undefined && request.currentReading !== null && request.currentReadingDate) {
      initialReadings.push({
        value: request.currentReading,
        date: Timestamp.fromDate(request.currentReadingDate),
        type: 'manual',
        notes: 'Initial reading'
      });
    }

    const meter: Meter = {
      meterUID,
      ...request,
      provider: toStoredMeterProvider(request.provider),
      currentReadingDate: request.currentReadingDate ? Timestamp.fromDate(request.currentReadingDate) : undefined,
      readings: initialReadings,
      documents: [],
      status: 'active',
      createdAt: now,
      updatedAt: now,
      deleted: false
    };

    this.mutate(request.propertyUID, list => sortByType([...list, meter]));
    return meterUID;
  }

  async updateMeter(meterUID: string, updates: UpdateMeterRequest): Promise<void> {
    const pid = this._currentPropertyUID();
    if (!pid) throw new Error('No property loaded');
    const now = Timestamp.now();
    this.mutate(pid, list =>
      list.map(m =>
        m.meterUID === meterUID
          ? {
              ...m,
              ...updates,
              // Mirror production: the provider object is replaced wholesale, and
              // its Date inputs are converted to Timestamps. Leave it untouched
              // when the update carries no provider key.
              provider: 'provider' in updates ? toStoredMeterProvider(updates.provider) : m.provider,
              updatedAt: now
            }
          : m
      )
    );
  }

  async deleteMeter(meterUID: string): Promise<void> {
    const pid = this._currentPropertyUID();
    if (!pid) throw new Error('No property loaded');
    const now = Timestamp.now();
    // Soft delete mirrors production: mark deleted + filter from the displayed list.
    this.mutate(pid, list => list.filter(m => m.meterUID !== meterUID).map(m => ({ ...m, updatedAt: now })));
  }

  async addReading(meterUID: string, reading: AddMeterReadingRequest): Promise<void> {
    const pid = this._currentPropertyUID();
    if (!pid) throw new Error('No property loaded');
    const now = Timestamp.now();
    const newReading: MeterReading = {
      ...reading,
      date: Timestamp.fromDate(reading.date)
    };
    this.mutate(pid, list =>
      list.map(m =>
        m.meterUID === meterUID
          ? {
              ...m,
              readings: [...m.readings, newReading],
              currentReading: reading.value,
              currentReadingDate: Timestamp.fromDate(reading.date),
              updatedAt: now
            }
          : m
      )
    );
  }

  async transferContract(meterUID: string, newHolder: ContractHolder): Promise<void> {
    await this.updateMeter(meterUID, { contractHolder: newHolder });
  }

  async addDocument(meterUID: string, document: Omit<MeterDocument, 'id' | 'uploadDate'>): Promise<void> {
    const pid = this._currentPropertyUID();
    if (!pid) throw new Error('No property loaded');
    const now = Timestamp.now();
    const newDocument: MeterDocument = {
      ...document,
      id: `mock-doc-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      uploadDate: now
    };
    this.mutate(pid, list =>
      list.map(m =>
        m.meterUID === meterUID
          ? { ...m, documents: [...m.documents, newDocument], updatedAt: now }
          : m
      )
    );
  }

  async addPhoto(meterUID: string, file: File): Promise<void> {
    return this.addFile(meterUID, file, 'photo');
  }

  async addFile(meterUID: string, file: File, type: MeterDocument['type']): Promise<void> {
    // No real storage in the mock — attach a placeholder document so the
    // in-memory meter reflects the new file for demos and consumer tests.
    await this.addDocument(meterUID, {
      name: file.name,
      type,
      url: `mock://documents/${file.name}`,
      size: file.size,
      mimeType: file.type,
      storagePath: `mock/documents/${meterUID}/${file.name}`
    });
  }

  async removeDocument(meterUID: string, documentId: string): Promise<void> {
    const pid = this._currentPropertyUID();
    if (!pid) throw new Error('No property loaded');
    const now = Timestamp.now();
    this.mutate(pid, list =>
      list.map(m =>
        m.meterUID === meterUID
          ? { ...m, documents: m.documents.filter(d => d.id !== documentId), updatedAt: now }
          : m
      )
    );
  }

  isEanCodeUnique(eanCode: string, excludeMeterUID?: string): boolean {
    if (!eanCode) return true;
    return !this.meters().some(
      m => m.eanCode === eanCode && (!excludeMeterUID || m.meterUID !== excludeMeterUID)
    );
  }

  getMeterById(meterUID: string): Meter | null {
    return this.meters().find(m => m.meterUID === meterUID) ?? null;
  }

  private mutate(propertyUID: string, fn: (list: readonly Meter[]) => readonly Meter[]): void {
    const map = new Map(this._metersByProperty());
    const current = map.get(propertyUID) ?? [];
    map.set(propertyUID, fn(current));
    this._metersByProperty.set(map);
  }
}

function sortByType(meters: readonly Meter[]): readonly Meter[] {
  return [...meters].sort((a, b) => a.type.localeCompare(b.type));
}

/** Small fixture so demos/Storybook don't start with an empty view. */
function seed(): ReadonlyMap<string, readonly Meter[]> {
  const now = Timestamp.now();
  const thirtyDaysAgo = Timestamp.fromMillis(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const demoPropertyUID = 'demo-property-1';
  return new Map<string, readonly Meter[]>([
    [
      demoPropertyUID,
      [
        {
          meterUID: 'demo-meter-electricity',
          propertyUID: demoPropertyUID,
          type: 'electricity',
          subType: 'dual_rate',
          eanCode: '5414489000000',
          meterNumber: 'E-1234567',
          location: 'Basement',
          contractHolder: 'tenant',
          currentReading: 15234,
          currentReadingDate: thirtyDaysAgo,
          readings: [
            { value: 15234, date: thirtyDaysAgo, type: 'manual' }
          ],
          documents: [],
          status: 'active',
          createdAt: now,
          updatedAt: now,
          deleted: false
        },
        {
          meterUID: 'demo-meter-water',
          propertyUID: demoPropertyUID,
          type: 'water',
          subType: 'cold_water',
          meterNumber: 'W-9876543',
          location: 'Kitchen',
          contractHolder: 'owner',
          currentReading: 842,
          currentReadingDate: thirtyDaysAgo,
          readings: [
            { value: 842, date: thirtyDaysAgo, type: 'manual' }
          ],
          documents: [],
          status: 'active',
          createdAt: now,
          updatedAt: now,
          deleted: false
        }
      ]
    ]
  ]);
}
